import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar, Box, Button, IconButton, Skeleton, Typography } from "@mui/material";

import { User } from "../../../../types/types";
import { getJobDetailsRoute, getProfileRoute } from "../../../../routers/routeUtils";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

import { CandidateDetails } from "../../types/candidate";
import { BackButtonIcon } from "../../assets/icons/candidate-details-page-icons";
import { Bucket } from "../../enum/jobEnums";

import ApplicantFormTab from "./Tabs/ApplicantFormTab";
import CandidateOverviewTab from "./Tabs/CandidateOverviewTab";
import CandidateDetailsNavigation from "./CandidateDetailsNavigation";
import CandidateDetailsLoading from "./CandidateDetailsLoading";
import { useGetCandidateApplication } from "../../api/queries/useGetCandidateApplication";
import { useUserProfile } from "../../../User/api/queries/useUserProfile";
import { useFollowMutation } from "../../../User/api/mutations/useFollowMutation";
import { useUnfollowMutation } from "../../../User/api/mutations/useUnfollowMutation";

const CandidateDetailsPage = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateDetailsPage" });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { jobId, candidateId } = useParams<{ jobId: string; candidateId: string }>();
  const { t: tLanguage } = useTranslation("goHire", { keyPrefix: "enum.language" });

  const currentTab = searchParams.get("tab") || "overview";

  const { data, isLoading } = useGetCandidateApplication({
    jobId,
    candidateId,
  });

  const { data: profileData } = useUserProfile(candidateId!);
  const { follow } = useFollowMutation();
  const { unfollow } = useUnfollowMutation();

  const candidateDetails = useMemo(() => {
    // ... (rest of useMemo remains same)
    if (!data?.data) return null;

    const application = data.data;
    const user = application.candidate;

    const details: CandidateDetails = {
      user,
      fitScore: application.fitScore.percent,
      fitScoreJustification: "", // This seems to be missing from the current response structure
      // @ts-ignore
      candidateStatus: application.status ?? Bucket.APPLIED, // Default or get from another source
      candidateOverview: [
        {
          key: "experience",
          value: t("experience", { count: application.workExperienceYrs }),
        },
        {
          key: "languages",
          value: application.languages.map((language) => tLanguage(language)).join(", "),
        },
      ],
      languagesSpoken: application.languages,
      willingnessToRelocate: application.relocate,
      introductionVideo: application.introVideoUrl
        ? {
            url: application.introVideoUrl,
            type: "VIDEO",
          }
        : (null as any),
      clubsAcademiesWorkedIn: application.clubsAndAcademies?.map((club: any) => club.name) || [],
      certificateAndLicenses:
        application.certificates?.map((cert: any) => ({
          title: cert.name,
          startDate: "",
          endDate: "",
        })) || [],
    };

    return details;
  }, [data, t]);

  const { fullName, firstTwoChr, role, countryName, media, profileLink, isFollowing } = useUserDataFormatter(
    (profileData || candidateDetails?.user || {}) as User
  );

  const handleViewProfile = () => navigate(profileLink);
  const handleNavigateBackToJobDetails = () => navigate(getJobDetailsRoute(jobId!));

  const handleFollowToggle = () => {
    if (!profileData) return;

    if (isFollowing) {
      unfollow(profileData);
    } else {
      follow(profileData);
    }
  };

  if (isLoading) {
    return <CandidateDetailsLoading />;
  }

  if (!candidateDetails) {
    return (
      <Box sx={styles.container}>
        <Typography>Candidate not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container} component="main">
      <Box sx={styles.header}>
        <IconButton size="small" onClick={handleNavigateBackToJobDetails}>
          <BackButtonIcon />
        </IconButton>
        <Typography sx={styles.title}>{t("applicantForm")}</Typography>
      </Box>
      <Box sx={styles.userInfoContainer}>
        <Box sx={styles.userInfo}>
          <Avatar src={media} sx={styles.avatar}>
            {firstTwoChr}
          </Avatar>
          <Box sx={styles.userInfoDetails}>
            <Typography sx={styles.fullName} component={Link} to={profileLink}>
              {fullName}
            </Typography>
            <Typography sx={styles.details}>
              {role} {countryName && `• ${countryName}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.buttons}>
          <Button sx={styles.viewProfileButton} variant="secondary-sm" onClick={handleViewProfile}>
            {t("viewProfile")}
          </Button>
          <Button variant="primary-sm" sx={styles.followButton} onClick={handleFollowToggle}>
            {isFollowing ? t("unfollow") : t("follow")}
          </Button>
        </Box>
      </Box>
      <CandidateDetailsNavigation />
      {currentTab === "overview" && <CandidateOverviewTab candidateDetails={candidateDetails} />}
      {currentTab === "applicant-form" && <ApplicantFormTab candidateDetails={candidateDetails} />}
    </Box>
  );
};

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  userInfoContainer: {
    display: "flex",
    gap: { xs: "24px", sm: "0" },
    flexDirection: { xs: "column", sm: "row" },
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  avatar: {
    width: "64px",
    height: "64px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  userInfoDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  fullName: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "32px",
    textDecoration: "none",
  },
  details: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  viewProfileButton: {
    color: "var(--Gray-700, #344054) !important",
    border: "1px solid var(--Gray-300, #D0D5DD) !important",
    background: "var(--Base-White, #FFF) !important",
    width: { xs: "100%", sm: "fit-content" },
  },
  buttons: {
    width: { xs: "100%", sm: "fit-content" },
    display: "flex",
    gap: "12px",
  },
  followButton: {
    width: { xs: "100%", sm: "fit-content" },
  },
};

export default CandidateDetailsPage;
